"use client";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";

import {
  HamburgerMenuIcon,
  MagnifyingGlassIcon,
  Cross1Icon,
} from "@radix-ui/react-icons";
import { Nav } from "@/types/Navigation";
import ProductSearch from "@/components/ProductSearch";
import { ActiveBanner } from "../types";
import SitewideBanner from "@/components/SitewideBanner";
import { useNavigationMessages } from "@/i18n/hooks/useNavigationMessages";

type Props = {
  navigation: Nav;
  openNav: boolean;
  setOpenNav: Function;
  activeBanner?: ActiveBanner;
  navButtonRef: React.RefObject<HTMLButtonElement | null>;
  bannerContainerClassname?: string;
};

export default function MobileNav({
  navigation,
  openNav,
  setOpenNav,
  activeBanner,
  navButtonRef,
  bannerContainerClassname = "",
}: Props) {
  const { header } = navigation;
  const navMessages = useNavigationMessages();
  const [showSearch, setShowSearch] = useState(false);

  return (
    <div className="grid grid-cols-3 md:hidden relative h-[76px] justify-center items-center border-b border-putty-400">
      <div className="pl-4 flex items-center gap-1">
        <button
          className="hover:bg-primary p-2 rounded-full hover:text-white"
          onClick={() => setOpenNav(!openNav)}
          aria-label={
            openNav
              ? navMessages.mobileNavMenuOpen
              : navMessages.mobileNavMenuClosed
          }
          ref={navButtonRef}
        >
          {openNav ? (
            <Cross1Icon height={20} width={20} />
          ) : (
            <HamburgerMenuIcon height={20} width={20} />
          )}
        </button>
      </div>
      <div className="flex justify-items-center justify-center">
        <Link href="/">
          <Image
            height={60}
            width={60}
            src={header.logo?.url}
            alt={header.logo?.altText ?? navMessages.logoAlt}
            title={header.logo?.altText ?? navMessages.logoTitle}
          />
        </Link>
      </div>
      <div className="pr-4 flex justify-items-end justify-end gap-2">
        <button
          className="hover:bg-primary p-2 rounded-full hover:text-white focus:border-gray-dark"
          onClick={() => setShowSearch(!showSearch)}
          aria-label={navMessages.searchAriaLabel}
        >
          {showSearch ? (
            <Cross1Icon height={24} width={24} />
          ) : (
            <MagnifyingGlassIcon height={24} width={24} />
          )}
        </button>
      </div>
      {showSearch && (
        <div className="bg-light absolute w-full -bottom-12 z-50">
          <ProductSearch
            variant="nav-header"
            placeholder={navMessages.searchMobilePlaceholder}
            embedded={false}
            location="nav-search"
            isMobileSearchExpanded
          />
        </div>
      )}
      <div
        className={`absolute top-full w-full z-40 ${bannerContainerClassname}`}
      >
        <SitewideBanner {...activeBanner} />
      </div>
    </div>
  );
}
